import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../config/URL";
import {
  Avatar,
  Box,
  Chip,
  Stack,
  Typography,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import NewConversationModal from "./NewConversation";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../../utility/mrtColumnPersistence";
import { toast } from "react-toastify";

const MyMessages = ({ fetchLeadCounts }) => {
  const MODULE_KEY = "MYMESSAGE";
  const navigate = useNavigate();

  const userId = localStorage.getItem("loginUserId");
  const userType = "USER";
  const [showNewModal, setShowNewModal] = useState(false);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  /* -------- SERVER SIDE PAGINATION STATE -------- */
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);

  /* -------- SAFE VALUES -------- */
  const safePageIndex = Number.isInteger(pageIndex) ? pageIndex : 0;
  const safePageSize = Number.isInteger(pageSize) ? pageSize : 20;
  const safeRowCount = Number.isInteger(rowCount) ? rowCount : 0;

  /* -------- DEFAULT COLUMN VISIBILITY -------- */
  const defaultColumnVisibility = {
    // participants: false,
    // lastMessagePreview: false,
    // lastMessageAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState(() =>
    getColumnPreference(MODULE_KEY, defaultColumnVisibility),
  );

  /* ---------------- FETCH MY CONVERSATIONS ---------------- */
  const getData = async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/myConversations?userId=${userId}&userType=${userType}&page=${
          safePageIndex + 1
        }&size=${safePageSize}`,
      );

      const conversations = res?.data?.data || [];

      const formatted = conversations.map((c) => ({
        conversationId: c.conversationId,
        participants: Array.isArray(c.participants) ? c.participants : [],
        lastMessagePreview: c.lastMessagePreview || "-",
        unreadCount: c.unreadCount || 0,
        lastMessageAt: c.lastMessageAt
          ? new Date(c.lastMessageAt)
              .toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })
              .toUpperCase()
          : "-",
      }));

      setData(formatted);
      setRowCount(res?.data?.totalElements ?? 0);
    } catch (err) {
      console.error("Failed to fetch conversations", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [safePageIndex, safePageSize]);

  /* ---------------- TABLE COLUMNS ---------------- */
  const columns = useMemo(
    () => [
      {
        accessorFn: (_, index) => index + 1 + safePageIndex * safePageSize,
        header: "S.NO",
        size: 60,
      },
      {
        accessorKey: "participants",
        header: "Participants",
        size: 300,
        Cell: ({ cell }) => {
          const participants = cell.getValue();

          if (!participants || participants.length === 0) {
            return <Typography color="text.secondary">-</Typography>;
          }

          return (
            <Stack spacing={1}>
              {participants.map((p) => (
                <Box
                  key={p.userId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    backgroundColor: "#f8fafc",
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  <Avatar
                    src={p.userProfile || ""}
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: "#287f71",
                      color: "#fff",
                      fontSize: 13,
                    }}
                  >
                    {!p.userProfile && p.userName?.charAt(0)}
                  </Avatar>

                  <Box>
                    <Typography fontSize={13} fontWeight={600}>
                      {p.userName}
                    </Typography>
                    <Chip
                      label={p.userType || p.userRole}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "10px",
                        backgroundColor: "#e6edf7",
                        color: "#287f71",
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          );
        },
      },
      {
        accessorKey: "lastMessagePreview",
        header: "Last Message",
        size: 260,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontSize={13} color="text.secondary" noWrap>
              {row.original.lastMessagePreview}
            </Typography>

            {row.original.unreadCount > 0 && (
              <Chip
                label={row.original.unreadCount}
                color="error"
                size="small"
                sx={{ height: 20, fontSize: "11px" }}
              />
            )}
          </Stack>
        ),
      },
      {
        accessorKey: "lastMessageAt",
        header: "Last Activity",
        size: 180,
      },
    ],
    [safePageIndex, safePageSize],
  );

  /* ---------------- THEME ---------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: "#e6edf7",
            fontSize: "13px",
            fontWeight: 500,
            textAlign: "center",
          },
        },
      },
    },
  });

  return (
    <div className="container-fluid my-3">
      <ol className="breadcrumb my-3 px-2">
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Messaging
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active">My Messages</li>
      </ol>

      <div className="card">
        <div
          className="mb-3 d-flex justify-content-between align-items-center p-2"
          style={{ background: "#f5f7f9" }}
        >
          <span className="text-muted">
            This database shows the list of{" "}
            <strong style={{ color: "#287f71" }}>My Conversations</strong>
          </span>
          <button
            className="btn btn-button"
            onClick={() => setShowNewModal(true)}
          >
            New Message
          </button>
        </div>

        <ThemeProvider theme={theme}>
          <MaterialReactTable
            columns={columns}
            data={data}
            manualPagination
            rowCount={safeRowCount}
            enableColumnActions
            enableColumnFilters={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            state={{
              isLoading: loading,
              pagination: {
                pageIndex: safePageIndex,
                pageSize: safePageSize,
              },
              columnVisibility,
            }}
            onColumnVisibilityChange={(updater) => {
              const newVisibility =
                typeof updater === "function"
                  ? updater(columnVisibility)
                  : updater;

              setColumnVisibility(newVisibility);
              saveColumnPreference(MODULE_KEY, newVisibility);
            }}
            onPaginationChange={(updater) => {
              const next =
                typeof updater === "function"
                  ? updater({
                      pageIndex: safePageIndex,
                      pageSize: safePageSize,
                    })
                  : updater;

              if (next.pageSize !== safePageSize) {
                setPageIndex(0);
              } else {
                setPageIndex(next.pageIndex);
              }

              setPageSize(next.pageSize);
            }}
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => {
                navigate(`/messaging/view/${row.original.conversationId}`, {
                  state: {
                    participants: row.original.participants,
                  },
                });
              },
              sx: { cursor: "pointer" },
            })}
          />
        </ThemeProvider>
      </div>

      <NewConversationModal
        show={showNewModal}
        onHide={() => setShowNewModal(false)}
      />
    </div>
  );
};

export default MyMessages;
